import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { compressImageFile } from "../../utils/compressImage";
import UploadIcon from "../../assets/image/upload.svg";
import {
  limitTextInput,
  validateWordCount,
  WORD_COUNT_HINT,
} from "../../utils/textWordValidation";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

/** Up to 4 digits before decimal, up to 2 after (e.g. 1234.12). */
const sanitizeExpenseAmount = (raw) => {
  const s = String(raw ?? "").replace(/[^0-9.]/g, "");
  const dot = s.indexOf(".");
  if (dot === -1) return s.slice(0, 4);
  const intPart = s.slice(0, dot).slice(0, 4);
  const decPart = s.slice(dot + 1).replace(/\./g, "").slice(0, 2);
  if (decPart.length > 0 || s.endsWith(".")) return `${intPart}.${decPart}`;
  return intPart;
};

const isValidExpenseAmount = (value) => {
  const s = String(value ?? "").trim();
  if (!s || s.endsWith(".")) return false;
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(s)) return false;
  const n = Number(s);
  return Number.isFinite(n) && n > 0;
};

const formatFileSize = (bytes) => {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AddNewExpense = () => {
  const navigate = useNavigate();
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const tripId = searchParams.get("trip_id")?.trim() || "";

  const fromSource = searchParams.get("from")?.trim() || "";

  const viewTripDetailsHref = useMemo(() => {
    const routeId = searchParams.get("route_id")?.trim() || "";
    const tid = searchParams.get("trip_id")?.trim() || "";
    const q = new URLSearchParams();
    if (tid) q.set("trip_id", tid);
    if (routeId) q.set("route_id", routeId);
    if (fromSource) q.set("from", fromSource);
    const qs = q.toString();
    return qs
      ? `/trip-assignment-management/view-trip-details?${qs}`
      : "/trip-assignment-management/view-trip-details";
  }, [searchParams, fromSource]);

  const postExpenseRedirectHref = useMemo(() => {
    if (fromSource === "trip-break") return "/trip-assignment-management";
    return viewTripDetailsHref;
  }, [fromSource, viewTripDetailsHref]);

  useEffect(() => {
    setSubheading("Add New Expense");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [compressingFile, setCompressingFile] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const receiptPreviewUrl = useMemo(() => {
    if (!file) return null;
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (receiptPreviewUrl) URL.revokeObjectURL(receiptPreviewUrl);
    };
  }, [receiptPreviewUrl]);

  const handleReceiptUpload = async (e) => {
    const selectedFile = e.target.files?.[0];
    e.target.value = null;
    if (!selectedFile) return;

    const allowedTypes = ["image/png", "image/jpeg"];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Only JPG or PNG images allowed for receipt.");
      return;
    }

    setFile(null);
    setCompressingFile(true);
    try {
      const compressed = await compressImageFile(selectedFile);
      setFile(compressed);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Could not compress image. Please try again.");
    } finally {
      setCompressingFile(false);
    }
  };

  const handleReceiptRemove = () => {
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tripId) {
      toast.error("Missing trip. Open this page from trip details.");
      return;
    }
    const amountTrim = String(amount).trim();
    if (!isValidExpenseAmount(amountTrim)) {
      toast.error("Enter amount up to 4 digits and 2 decimals (e.g. 1234.12).");
      return;
    }
    const descTrim = description.trim();
    const descriptionError = validateWordCount(descTrim, "Description");
    if (descriptionError) {
      toast.error(descriptionError);
      return;
    }
    if (!file) {
      toast.error("Upload a receipt image.");
      return;
    }

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    const formData = new FormData();
    formData.append("trip_id", tripId);
    formData.append("amount", amountTrim);
    formData.append("description", descTrim);
    formData.append("file", file, file.name || "receipt.jpg");

    setSubmitting(true);
    const url = `${ACCOUNTANT_API}${endpoint.addTripExpence}`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const payload = await parseBody(response);

      if (!response.ok) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      const apiCode = payload?.code;
      if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      toast.success(payload?.message || "Expense added successfully.");
      navigate(postExpenseRedirectHref, { replace: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not add expense."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <h3 className="text-xl font-UbuntuMedium mt-7 !mb-2">Add Expense Details</h3>
      <div className="row col-md-12 bg-white mx-0 p-4 shadow-sm">
        <form onSubmit={handleSubmit} className="">
          <div className="grid items-stretch gap-6 md:grid-cols-2">
            <div className="flex h-full flex-col gap-4">
              <div>
                <label className="block text-sm text-gray-500 mb-1">Expense</label>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="Amount Spent"
                  value={amount}
                  onChange={(e) => setAmount(sanitizeExpenseAmount(e.target.value))}
                  disabled={submitting}
                  maxLength={7}
                  title="Up to 4 digits before decimal and 2 after"
                  className="w-full border rounded-lg px-4 py-2 outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              <div className="flex min-h-[260px] flex-1 flex-col">
                <textarea
                  placeholder="Description"
                  value={description}
                  onChange={(e) => setDescription(limitTextInput(e.target.value))}
                  disabled={submitting}
                  className="h-full min-h-[260px] w-full flex-1 resize-none rounded-lg border px-4 py-3 outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p className="mt-1 text-xs text-gray-500">{WORD_COUNT_HINT}</p>
              </div>
            </div>

            <div className="flex h-full flex-col">
              <label className="mb-2 block text-sm !text-primary">Upload Receipt*</label>

              <div className="flex min-h-[260px] flex-1 flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center">
                {compressingFile ? (
                  <p className="text-sm text-gray-600">Compressing image…</p>
                ) : file && receiptPreviewUrl ? (
                  <div className="group relative w-full max-w-[260px] overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-md ring-1 ring-black/5 transition hover:shadow-lg hover:ring-black/10">
                    <button
                      type="button"
                      aria-label="Remove receipt"
                      className="absolute right-2 top-2 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-base leading-none text-gray-500 shadow-md backdrop-blur-sm transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={handleReceiptRemove}
                      disabled={submitting}
                    >
                      ×
                    </button>
                    <div className="relative aspect-[4/5] w-full bg-gray-100">
                      <img
                        src={receiptPreviewUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/35 to-transparent px-2 pb-2 pt-10">
                        <p className="truncate text-center text-[11px] font-medium text-white drop-shadow-sm">
                          {file.name}
                        </p>
                        <p className="text-center text-[10px] text-white/85">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="flex h-40 w-64 cursor-pointer items-center justify-center rounded-lg px-2 text-center transition hover:bg-gray-50">
                    <span className="flex items-center text-sm">
                      <img src={UploadIcon} className="mr-2 shrink-0" alt="" />
                      Upload Image
                    </span>
                    <input
                      type="file"
                      hidden
                      accept="image/png,image/jpeg"
                      onChange={handleReceiptUpload}
                      disabled={submitting || compressingFile}
                    />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Link to={viewTripDetailsHref} className="btn btn-primary">
              Cancel
            </Link>
            <button
              type="submit"
              className="btn btn-danger ml-2"
              disabled={submitting || compressingFile || !tripId}
            >
              {submitting ? "Submitting…" : "Submit"}
            </button>
          </div>
        </form>
      </div>
      <Toastify />
    </>
  );
};

export default AddNewExpense;
